/**
 * Document scanner using OpenCV.js
 *
 * Detection: two strategies tried in order —
 *   1. Canny edge detection (best for high-contrast edges)
 *   2. Adaptive threshold (fallback for soft/webcam edges)
 * Both: downscale → preprocess → binary → contours → approxPolyDP → 4-pt quad
 */

const DETECT_MAX_DIM = 640;
const MIN_AREA_RATIO = 0.08;
const CORNER_SHRINK = 0.015;
// Reject candidates whose interior is darker than this (0–255). Paper sits
// well above ~140 even in shadow; dark mousepads/desks fall below 90.
const MIN_PAPER_BRIGHTNESS = 110;
// Penalise non-rectangular quads (e.g. a paper+mousepad blob with a kink in
// one corner). 1.0 = perfect rectangle, ~0.6 = badly skewed.
const MIN_RECTANGULARITY = 0.65;

function distance(p1, p2) {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y);
}

// Image coords are y-down, so for diff = x - y: top-right has the largest
// diff (x high, y low) and bottom-left has the smallest (x low, y high).
function orderPoints(pts) {
  const sums = pts.map(p => p.x + p.y);
  const diffs = pts.map(p => p.x - p.y);
  return {
    topLeft: pts[sums.indexOf(Math.min(...sums))],
    bottomRight: pts[sums.indexOf(Math.max(...sums))],
    topRight: pts[diffs.indexOf(Math.max(...diffs))],
    bottomLeft: pts[diffs.indexOf(Math.min(...diffs))],
  };
}

// Pull corners slightly inward so we exclude adjacent surfaces (e.g. the
// notebook binding) that hug the paper edge.
function shrinkCorners(pts, ratio) {
  const cx = (pts[0].x + pts[1].x + pts[2].x + pts[3].x) / 4;
  const cy = (pts[0].y + pts[1].y + pts[2].y + pts[3].y) / 4;
  return pts.map(p => ({
    x: p.x + (cx - p.x) * ratio,
    y: p.y + (cy - p.y) * ratio,
  }));
}

function ensureOdd(n) {
  return n % 2 === 0 ? n + 1 : n;
}

// Score how rectangular a quad is by comparing its area to the area of its
// minimum-area enclosing rectangle. Returns 0..1 (1 = perfect rectangle).
function rectangularity(pts) {
  const a = polygonArea(pts);
  const r = minAreaRectArea(pts);
  return r > 0 ? Math.min(1, a / r) : 0;
}

function polygonArea(pts) {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    s += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return Math.abs(s) / 2;
}

// Rotating-calipers min-area rect on 4 points. Good enough for our quads
// without pulling cv.minAreaRect into a separate Mat allocation.
function minAreaRectArea(pts) {
  let minArea = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const dx = pts[j].x - pts[i].x;
    const dy = pts[j].y - pts[i].y;
    const len = Math.hypot(dx, dy);
    if (len === 0) continue;
    const ux = dx / len;
    const uy = dy / len;
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (const p of pts) {
      const u = p.x * ux + p.y * uy;
      const v = -p.x * uy + p.y * ux;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const area = (maxU - minU) * (maxV - minV);
    if (area < minArea) minArea = area;
  }
  return minArea === Infinity ? 0 : minArea;
}

export class Scanner {

  /**
   * Detect document corners. Returns ordered corner points or null.
   * Tries Canny-based detection first, falls back to threshold-based.
   */
  detectCorners(img) {
    const cv = window.cv;
    const scale = Math.min(1, DETECT_MAX_DIM / Math.max(img.cols, img.rows));
    const mats = [];

    try {
      // Downscale
      const small = new cv.Mat(); mats.push(small);
      if (scale < 1) {
        cv.resize(img, small, new cv.Size(0, 0), scale, scale, cv.INTER_AREA);
      } else {
        img.copyTo(small);
      }

      // Grayscale + blur (shared by both strategies)
      const gray = new cv.Mat(); mats.push(gray);
      cv.cvtColor(small, gray, cv.COLOR_RGBA2GRAY);
      const blurred = new cv.Mat(); mats.push(blurred);
      cv.GaussianBlur(gray, blurred, new cv.Size(7, 7), 0);

      // Strategy 1: Canny edges
      const cannyResult = this._detectFromEdges(cv, blurred, gray, scale, mats);
      if (cannyResult) return cannyResult;

      // Strategy 2: Adaptive threshold (better for webcams / soft edges)
      const threshResult = this._detectFromThreshold(cv, blurred, gray, scale, mats);
      return threshResult;
    } finally {
      mats.forEach(m => { try { m.delete(); } catch (_) { /* */ } });
    }
  }

  /** Canny-based edge detection → contour search */
  _detectFromEdges(cv, blurred, gray, scale, mats) {
    // Morph close to merge text into solid regions
    const closeKernel = cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(5, 5));
    mats.push(closeKernel);
    const closed = new cv.Mat(); mats.push(closed);
    cv.morphologyEx(blurred, closed, cv.MORPH_CLOSE, closeKernel, new cv.Point(-1, -1), 2);

    const edges = new cv.Mat(); mats.push(edges);
    cv.Canny(closed, edges, 40, 120);

    // Small dilation to connect nearby edge fragments
    const dilateKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3));
    mats.push(dilateKernel);
    cv.dilate(edges, edges, dilateKernel);

    return this._findQuad(cv, edges, gray, scale, mats);
  }

  /** Adaptive threshold detection — robust for webcams with uneven lighting */
  _detectFromThreshold(cv, blurred, gray, scale, mats) {
    const thresh = new cv.Mat(); mats.push(thresh);
    cv.adaptiveThreshold(blurred, thresh, 255,
      cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 11, 2);

    // Invert so document is white on black background
    const inverted = new cv.Mat(); mats.push(inverted);
    cv.bitwise_not(thresh, inverted);

    // Close small holes, open to remove noise
    const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3));
    mats.push(kernel);
    cv.morphologyEx(inverted, inverted, cv.MORPH_CLOSE, kernel, new cv.Point(-1, -1), 2);
    cv.morphologyEx(inverted, inverted, cv.MORPH_OPEN, kernel, new cv.Point(-1, -1), 1);

    return this._findQuad(cv, inverted, gray, scale, mats);
  }

  /**
   * Shared: find the best 4-point convex quad in a binary image.
   * Scores candidates by area × brightness² × rectangularity and rejects
   * dark or non-rectangular regions, so paper wins over a paper+mousepad blob
   * even when the merged blob has larger raw area.
   */
  _findQuad(cv, binary, gray, scale, mats) {
    const bordered = new cv.Mat(); mats.push(bordered);
    cv.copyMakeBorder(binary, bordered, 5, 5, 5, 5, cv.BORDER_CONSTANT, new cv.Scalar(0));
    const grayBordered = new cv.Mat(); mats.push(grayBordered);
    cv.copyMakeBorder(gray, grayBordered, 5, 5, 5, 5, cv.BORDER_CONSTANT, new cv.Scalar(0));

    const contours = new cv.MatVector(); mats.push(contours);
    const hierarchy = new cv.Mat(); mats.push(hierarchy);
    cv.findContours(bordered, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

    const imageArea = bordered.rows * bordered.cols;
    const candidates = [];
    for (let i = 0; i < contours.size(); i++) {
      candidates.push({ index: i, area: cv.contourArea(contours.get(i)) });
    }
    candidates.sort((a, b) => b.area - a.area);

    let bestPts = null;
    let bestScore = 0;

    for (const c of candidates.slice(0, 8)) {
      if (c.area < imageArea * MIN_AREA_RATIO) continue;

      const contour = contours.get(c.index);
      const hull = new cv.Mat(); mats.push(hull);
      cv.convexHull(contour, hull);
      const peri = cv.arcLength(hull, true);

      for (const eps of [0.02, 0.04, 0.06]) {
        const approx = new cv.Mat(); mats.push(approx);
        cv.approxPolyDP(hull, approx, eps * peri, true);
        if (approx.rows !== 4) continue;

        const borderedPts = [];
        for (let j = 0; j < 4; j++) {
          borderedPts.push({
            x: approx.data32S[j * 2],
            y: approx.data32S[j * 2 + 1],
          });
        }

        const brightness = this._meanInsideQuad(cv, grayBordered, approx, mats);
        if (brightness < MIN_PAPER_BRIGHTNESS) break;

        const rect = rectangularity(borderedPts);
        if (rect < MIN_RECTANGULARITY) break;

        const score = c.area * Math.pow(brightness / 255, 2) * rect;
        if (score > bestScore) {
          bestScore = score;
          bestPts = borderedPts.map(p => ({
            x: (p.x - 5) / scale,
            y: (p.y - 5) / scale,
          }));
        }
        break;
      }
    }

    return bestPts ? orderPoints(shrinkCorners(bestPts, CORNER_SHRINK)) : null;
  }

  /** Mean grayscale value inside the polygon defined by `approx` (4-pt Mat). */
  _meanInsideQuad(cv, grayBordered, approx, mats) {
    const mask = cv.Mat.zeros(grayBordered.rows, grayBordered.cols, cv.CV_8UC1);
    mats.push(mask);
    const polyVec = new cv.MatVector();
    polyVec.push_back(approx);
    cv.drawContours(mask, polyVec, 0, new cv.Scalar(255), -1);
    polyVec.delete();
    return cv.mean(grayBordered, mask)[0];
  }

  /**
   * Draw detected document outline on a canvas.
   */
  highlightPaper(image, options = {}) {
    const { color = '#c4b5ff', thickness = 3 } = options;
    const cv = window.cv;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = cv.imread(image);

    try {
      cv.imshow(canvas, img);
      const corners = this.detectCorners(img);
      if (corners) {
        const { topLeft: tl, topRight: tr, bottomRight: br, bottomLeft: bl } = corners;
        ctx.strokeStyle = color;
        ctx.lineWidth = thickness;
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(tl.x, tl.y);
        ctx.lineTo(tr.x, tr.y);
        ctx.lineTo(br.x, br.y);
        ctx.lineTo(bl.x, bl.y);
        ctx.closePath();
        ctx.stroke();
      }
      return { canvas, corners };
    } finally {
      img.delete();
    }
  }

  /**
   * Extract and perspective-correct the detected document.
   */
  extractPaper(image, cornerPoints, options = {}) {
    const { preserveColor = true } = options;
    const cv = window.cv;
    const canvas = document.createElement('canvas');
    const img = cv.imread(image);
    const mats = [];

    try {
      const corners = cornerPoints || this.detectCorners(img);
      if (!corners) return null;

      const { topLeft: tl, topRight: tr, bottomRight: br, bottomLeft: bl } = corners;
      const maxWidth = Math.round(Math.max(distance(tl, tr), distance(bl, br)));
      const maxHeight = Math.round(Math.max(distance(tl, bl), distance(tr, br)));

      const srcPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
        tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y,
      ]); mats.push(srcPts);
      const dstPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
        0, 0, maxWidth - 1, 0, maxWidth - 1, maxHeight - 1, 0, maxHeight - 1,
      ]); mats.push(dstPts);

      const M = cv.getPerspectiveTransform(srcPts, dstPts); mats.push(M);
      const warped = new cv.Mat(); mats.push(warped);
      cv.warpPerspective(img, warped, M, new cv.Size(maxWidth, maxHeight),
        cv.INTER_CUBIC, cv.BORDER_CONSTANT, new cv.Scalar());

      const enhanced = this.enhanceDocument(warped, { preserveColor });
      cv.imshow(canvas, enhanced);
      enhanced.delete();
      return { canvas, corners };
    } finally {
      img.delete();
      mats.forEach(m => { try { m.delete(); } catch (_) { /* */ } });
    }
  }

  /**
   * Enhance document — normalize lighting, whiten background, darken text.
   * preserveColor: true → LAB color space (keeps ink colors), false → grayscale.
   */
  enhanceDocument(src, options = {}) {
    const { preserveColor = true } = options;
    const cv = window.cv;
    const mats = [];

    try {
      const kernelSize = ensureOdd(Math.max(21, Math.round(src.cols / 30)));
      const blockSize = ensureOdd(Math.max(15, Math.round(src.cols / 100)));

      if (preserveColor && src.channels() >= 3) {
        return this._enhanceColor(src, kernelSize, blockSize, mats);
      }
      return this._enhanceGrayscale(src, kernelSize, blockSize, mats);
    } catch (err) {
      console.error('Enhancement failed:', err);
      const fallback = new cv.Mat();
      src.copyTo(fallback);
      return fallback;
    } finally {
      mats.forEach(m => { try { m.delete(); } catch (_) { /* */ } });
    }
  }

  _enhanceColor(src, kernelSize, blockSize, mats) {
    const cv = window.cv;
    const bgr = new cv.Mat(); mats.push(bgr);
    cv.cvtColor(src, bgr, cv.COLOR_RGBA2BGR);
    const lab = new cv.Mat(); mats.push(lab);
    cv.cvtColor(bgr, lab, cv.COLOR_BGR2Lab);

    const channels = new cv.MatVector();
    cv.split(lab, channels);
    const L = channels.get(0).clone(); mats.push(L);
    const A = channels.get(1).clone(); mats.push(A);
    const B = channels.get(2).clone(); mats.push(B);
    channels.delete();

    const enhancedL = this._enhanceLightness(L, kernelSize, blockSize, mats);
    mats.push(enhancedL);

    const merged = new cv.MatVector();
    merged.push_back(enhancedL);
    merged.push_back(A);
    merged.push_back(B);
    const enhancedLab = new cv.Mat(); mats.push(enhancedLab);
    cv.merge(merged, enhancedLab);
    merged.delete();

    const enhancedBgr = new cv.Mat(); mats.push(enhancedBgr);
    cv.cvtColor(enhancedLab, enhancedBgr, cv.COLOR_Lab2BGR);
    const result = new cv.Mat();
    cv.cvtColor(enhancedBgr, result, cv.COLOR_BGR2RGBA);
    return result;
  }

  _enhanceGrayscale(src, kernelSize, blockSize, mats) {
    const cv = window.cv;
    const gray = new cv.Mat(); mats.push(gray);
    if (src.channels() === 4) cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    else if (src.channels() === 3) cv.cvtColor(src, gray, cv.COLOR_RGB2GRAY);
    else src.copyTo(gray);

    const enhanced = this._enhanceLightness(gray, kernelSize, blockSize, mats);
    mats.push(enhanced);
    const result = new cv.Mat();
    cv.cvtColor(enhanced, result, cv.COLOR_GRAY2RGBA);
    return result;
  }

  _enhanceLightness(gray, kernelSize, blockSize, mats) {
    const cv = window.cv;

    const kernel = cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(kernelSize, kernelSize));
    mats.push(kernel);
    const bg = new cv.Mat(); mats.push(bg);
    cv.morphologyEx(gray, bg, cv.MORPH_CLOSE, kernel);

    const grayF = new cv.Mat(); mats.push(grayF);
    const bgF = new cv.Mat(); mats.push(bgF);
    gray.convertTo(grayF, cv.CV_32F);
    bg.convertTo(bgF, cv.CV_32F);
    const ones = new cv.Mat(bgF.rows, bgF.cols, cv.CV_32F, new cv.Scalar(1));
    mats.push(ones);
    cv.add(bgF, ones, bgF);
    const divided = new cv.Mat(); mats.push(divided);
    cv.divide(grayF, bgF, divided, 255.0);

    const normalized = new cv.Mat(); mats.push(normalized);
    divided.convertTo(normalized, cv.CV_8U);
    cv.normalize(normalized, normalized, 0, 255, cv.NORM_MINMAX);

    const thresh = new cv.Mat(); mats.push(thresh);
    cv.adaptiveThreshold(normalized, thresh, 255,
      cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, blockSize, 10);

    const textMask = new cv.Mat(); mats.push(textMask);
    cv.bitwise_not(thresh, textMask);

    const result = new cv.Mat(gray.rows, gray.cols, cv.CV_8UC1, new cv.Scalar(255));
    const darkened = new cv.Mat(); mats.push(darkened);
    normalized.convertTo(darkened, -1, 1.2, -30);
    cv.threshold(darkened, darkened, 255, 255, cv.THRESH_TRUNC);
    const zeros = new cv.Mat(gray.rows, gray.cols, cv.CV_8UC1, new cv.Scalar(0));
    mats.push(zeros);
    cv.max(darkened, zeros, darkened);
    darkened.copyTo(result, textMask);

    const final = new cv.Mat();
    cv.GaussianBlur(result, final, new cv.Size(3, 3), 0);
    result.delete();
    return final;
  }
}
